import type { PageTestSource, QualityOverview, RunPhase, TestRun } from "@/lib/qa-types";
import { phaseLabel } from "@/lib/demo-data";

interface OverviewViewProps {
    overview: QualityOverview;
    runPhase: RunPhase;
    onStartRun: () => void;
    onOpenGenerator: () => void;
    source: PageTestSource | null;
    caseCount: number;
    run: TestRun | null;
}

const recentRuns = [
    { id: "RUN-0818-1042", name: "商城结算核心回归", status: "通过", detail: "18/18 · 自愈 1", tone: "good" },
    { id: "RUN-0818-1038", name: "会员登录冒烟测试", status: "失败", detail: "7/8 · QA-1042", tone: "bad" },
    { id: "RUN-0818-1031", name: "优惠券边界组合", status: "通过", detail: "24/24 · 3m 42s", tone: "good" },
];

export function OverviewView({ overview, runPhase, onStartRun, onOpenGenerator, source, caseCount, run }: OverviewViewProps) {
    const isRunning = runPhase !== "idle" && runPhase !== "passed";
    return (
        <>
            <section className="hero-row">
                <div>
                    <p className="eyebrow">QUALITY COMMAND CENTER</p>
                    <h1>晚上好，林晓。</h1>
                    <p className="hero-copy">所有执行节点运行平稳。UAT 主链路最近 24 小时通过率上升 1.9%。</p>
                </div>
                <div className="hero-actions">
                    <button className="button secondary" onClick={onOpenGenerator}>
                        导入需求
                    </button>
                    <button className="button primary" disabled={isRunning} onClick={onStartRun}>
                        {isRunning ? "执行中…" : "运行完整演示"}
                    </button>
                </div>
            </section>

            {source && (
                <section className="card pipeline-report-card">
                    <div>
                        <p className="section-kicker">LATEST PAGE TEST REPORT</p>
                        <h2>{source.title}</h2>
                        <a href={source.target_url} rel="noreferrer" target="_blank">
                            {source.target_url}
                        </a>
                        <p>{source.requirement}</p>
                    </div>
                    <div className="pipeline-report-metrics">
                        <span>
                            页面特性 <strong>{source.characteristics.length}</strong>
                        </span>
                        <span>
                            测试场景 <strong>{caseCount}</strong>
                        </span>
                        <span>
                            执行状态 <strong>{run?.status ?? "待执行"}</strong>
                        </span>
                        <span>
                            运行编号 <strong>{run?.run_id ?? "—"}</strong>
                        </span>
                    </div>
                </section>
            )}

            <section className="metrics" aria-label="核心质量指标">
                <Metric label="用例通过率" value={`${overview.pass_rate}%`} delta="↑ 1.9%" tone="cyan" />
                <Metric label="在线 Agent" value={`${overview.active_agents}/3`} delta="2 个可调度" tone="purple" />
                <Metric label="今日自愈" value={String(overview.healed_steps)} delta="成功率 92.7%" tone="green" />
                <Metric label="待处理缺陷" value={String(overview.open_defects)} delta="3 个 P0 / P1" tone="orange" />
            </section>

            <section className="workflow-card" aria-live="polite">
                <div className="workflow-copy">
                    <span className={`pulse-dot ${isRunning ? "active" : ""}`} />
                    <div>
                        <p className="section-kicker">端到端演示链路</p>
                        <strong>{phaseLabel(runPhase)}</strong>
                    </div>
                </div>
                <div className="workflow-steps">
                    {[
                        ["01", "需求解析"],
                        ["02", "用例生成"],
                        ["03", "任务调度"],
                        ["04", "视觉执行"],
                        ["05", "自愈判定"],
                        ["06", "质量回传"],
                    ].map(([step, label], index) => (
                        <div
                            className={runPhase !== "idle" && index <= phaseIndex(runPhase) ? "workflow-step current" : "workflow-step"}
                            key={step}
                        >
                            <span>{step}</span>
                            <small>{label}</small>
                        </div>
                    ))}
                </div>
                {runPhase === "passed" && (
                    <div className="healing-result">
                        <span className="result-mark">✓</span>
                        <div>
                            <strong>定位路径已安全修复</strong>
                            <p>button#submit-order → role=button, name=确认下单</p>
                        </div>
                        <div className="score-pair">
                            <span>视觉 0.93</span>
                            <span>语义 0.96</span>
                        </div>
                    </div>
                )}
            </section>

            <div className="dashboard-grid">
                <section className="card chart-card">
                    <CardHead kicker="近 7 天" title="通过率趋势" action="查看报告" />
                    <div className="chart-area" aria-label="通过率趋势图">
                        <div className="chart-guides">
                            <span>100%</span>
                            <span>90%</span>
                            <span>80%</span>
                        </div>
                        <div className="bars">
                            {overview.pass_rate_trend.map((value, index) => (
                                <div className="bar-column" key={index}>
                                    <div className="bar" style={{ height: `${value - 60}%` }} />
                                    <small>{index + 12}日</small>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
                <section className="card coverage-card">
                    <CardHead kicker="质量门禁" title="代码覆盖率" action="78.4%" />
                    <div className="coverage-ring" style={{ "--coverage": `${overview.coverage * 3.6}deg` } as React.CSSProperties}>
                        <div>
                            <strong>{overview.coverage}%</strong>
                            <span>目标 80%</span>
                        </div>
                    </div>
                    <div className="coverage-legend">
                        <span>语句 82%</span>
                        <span>分支 74%</span>
                        <span>函数 79%</span>
                    </div>
                </section>
            </div>

            <section className="card runs-card">
                <CardHead kicker="实时活动" title="最近执行" action="全部执行记录" />
                <div className="run-table">
                    {run && source && (
                        <div className="run-row">
                            <div className="run-icon">▶</div>
                            <div className="run-name">
                                <strong>{source.title} · 页面测试</strong>
                                <span>
                                    {run.run_id} · {run.environment.toUpperCase()}
                                </span>
                            </div>
                            <span className={`status-pill ${run.status === "PASSED" ? "good" : "bad"}`}>{run.status}</span>
                            <span className="run-detail">
                                {run.case_ids.length}/{run.case_ids.length} · 来源页面
                            </span>
                            <button aria-label={`查看 ${source.title}`}>···</button>
                        </div>
                    )}
                    {recentRuns.map((run) => (
                        <div className="run-row" key={run.id}>
                            <div className="run-icon">▶</div>
                            <div className="run-name">
                                <strong>{run.name}</strong>
                                <span>{run.id} · UAT</span>
                            </div>
                            <span className={`status-pill ${run.tone}`}>{run.status}</span>
                            <span className="run-detail">{run.detail}</span>
                            <button aria-label={`查看 ${run.name}`}>···</button>
                        </div>
                    ))}
                </div>
            </section>
        </>
    );
}

function Metric({ label, value, delta, tone }: { label: string; value: string; delta: string; tone: string }) {
    return (
        <article className={`metric ${tone}`}>
            <div className="metric-icon" />
            <p>{label}</p>
            <strong>{value}</strong>
            <span>{delta}</span>
        </article>
    );
}

function CardHead({ kicker, title, action }: { kicker: string; title: string; action: string }) {
    return (
        <header className="card-head">
            <div>
                <p>{kicker}</p>
                <h2>{title}</h2>
            </div>
            <button>
                {action} <span>→</span>
            </button>
        </header>
    );
}

function phaseIndex(phase: RunPhase): number {
    return { idle: -1, queued: 1, running: 3, healing: 4, passed: 5 }[phase];
}
